import { createContext, useContext, useEffect } from "react";
import type { ReactNode } from "react";
import Animated, {
  measure,
  useAnimatedRef,
  useAnimatedStyle,
  useFrameCallback,
  useSharedValue,
} from "react-native-reanimated";
import type { SharedValue } from "react-native-reanimated";
import { StyleSheet } from "react-native";

/**
 * Lets a content-sized sheet close in around its content without the
 * native sheet resizing.
 *
 * The native sheet (@swmansion/react-native-bottom-sheet) sizes itself to
 * its content and eases to each new height on its own curve, while the
 * content is already laid out at the new height from the sheet's top: the
 * Pay button, at the bottom, would jump up with the content and ride down
 * with the sheet. So while held, the content keeps the height it had and
 * sits at its bottom; what folds away above it leaves room at the top, and
 * the sheet's drawn top — its surface and handle, which the app draws —
 * moves down by exactly that room, frame by frame. The native sheet never
 * shrinks; the button never moves. Released once the content fills the
 * height again (a refused payment unfolded), or with the screen.
 */

/** How far down from the native sheet's top the sheet is drawn. */
const SheetInset = createContext<SharedValue<number> | null>(null);

export const SheetInsetProvider = SheetInset.Provider;

/** The sheet is grown to its room (the keyboard is up in a vendor's
 *  checkout, TicketsSheet): the checkout fills it, its form taking what the
 *  keyboard leaves, the button still at the foot. */
export const SheetGrow = createContext(false);

export function SheetHold({
  hold,
  children,
}: {
  /** Keep the height: content is about to fold away, or has. */
  hold: boolean;
  children: ReactNode;
}) {
  const inset = useContext(SheetInset);
  const grow = useContext(SheetGrow);
  const fill = grow ? styles.grow : styles.shrink;
  const outer = useAnimatedRef<Animated.View>();
  const inner = useAnimatedRef<Animated.View>();
  const holding = useSharedValue(hold);
  // The height kept while held; 0 when free.
  const kept = useSharedValue(0);
  useEffect(() => {
    holding.set(hold);
  }, [hold, holding]);
  useFrameCallback(() => {
    const content = measure(inner);
    if (!content) return;
    if (kept.get() === 0) {
      if (!holding.get()) return;
      const box = measure(outer);
      if (box) kept.set(box.height);
    }
    // Released once the content is back to the height kept for it.
    if (!holding.get() && content.height >= kept.get() - 0.5) {
      kept.set(0);
      inset?.set(0);
      return;
    }
    inset?.set(Math.max(0, kept.get() - content.height));
  });
  const box = useAnimatedStyle(() =>
    kept.get() > 0
      ? { justifyContent: "flex-end", minHeight: kept.get() }
      : { justifyContent: "flex-start", minHeight: 0 },
  );
  // Shrinkable, so a checkout kept to the sheet's room (TicketsSheet's
  // `keyboard="content"`) can scroll its form inside; content-sized, it
  // never shrinks.
  return (
    <Animated.View ref={outer} style={[fill, box]}>
      <Animated.View ref={inner} style={fill}>
        {children}
      </Animated.View>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  shrink: { flexShrink: 1 },
  grow: { flexGrow: 1, flexShrink: 1 },
});
